import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tab,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import ScrollableTabs from '@components/mobile/ScrollableTabs';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import {
  ArrowBack,
  AccountBalance,
  Send,
  EventBusy
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/EmptyState';

const LEAVE_TYPES = ['casual', 'sick', 'earned'];

const LeaveManagement = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Balance state
  const [balances, setBalances] = useState([]);

  // Requests state
  const [requests, setRequests] = useState([]);
  const [applyForm, setApplyForm] = useState({
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchBalances = useCallback(async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getLeaveBalance();
      setBalances(response.data.balances || []);
    } catch (error) {
      showSnackbar('Error fetching leave balance', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getLeaveRequests();
      setRequests(response.data.requests || []);
    } catch (error) {
      showSnackbar('Error fetching leave requests', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load: fetch both so both tabs have data ready
  useEffect(() => {
    fetchBalances();
    fetchRequests();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch the relevant data when switching tabs to always show fresh numbers
  useEffect(() => {
    if (activeTab === 0) {
      fetchBalances();
    } else {
      fetchRequests();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyLeave = async () => {
    try {
      const { leave_type, start_date, end_date, reason } = applyForm;
      if (!leave_type || !start_date || !end_date || !reason) {
        showSnackbar('Please fill all fields', 'error');
        return;
      }
      if (new Date(end_date) < new Date(start_date)) {
        showSnackbar('End date cannot be before start date', 'error');
        return;
      }
      setSubmitting(true);
      await teacherAPI.applyLeave(applyForm);
      showSnackbar('Leave request submitted successfully');
      setApplyForm({ leave_type: 'casual', start_date: '', end_date: '', reason: '' });
      fetchRequests();
      fetchBalances();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error applying for leave', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    const config = {
      pending: { color: 'warning', label: 'Pending' },
      approved: { color: 'success', label: 'Approved' },
      rejected: { color: 'error', label: 'Rejected' }
    };
    const c = config[status] || { color: 'default', label: status };
    return <Chip size="small" color={c.color} label={c.label} />;
  };

  const getBalanceColor = (used, total) => {
    if (total === 0) return 'text.secondary';
    const ratio = used / total;
    if (ratio >= 1) return 'error.main';
    if (ratio >= 0.75) return 'warning.main';
    return 'success.main';
  };

  if (loading && balances.length === 0 && requests.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold">Leave Management</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/teacher/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3, p: 0.75 }}>
        <ScrollableTabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            minHeight: 0,
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              minHeight: 0,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
              },
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }
            }
          }}
        >
          <Tab icon={<AccountBalance />} label="My Balance" iconPosition="start" />
          <Tab icon={<EventBusy />} label="Apply & History" iconPosition="start" />
        </ScrollableTabs>
      </Paper>

      {/* Tab 0: My Balance */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {LEAVE_TYPES.map(type => {
            const bal = balances.find(b => b.leave_type === type);
            const total = bal ? bal.total_leaves : 0;
            const used = bal ? bal.used_leaves : 0;
            const remaining = total - used;
            // Pending days = sum of num_days for pending requests of this type
            const pendingDays = requests
              .filter(r => r.leave_type === type && r.status === 'pending')
              .reduce((sum, r) => sum + r.num_days, 0);

            return (
              <Grid item xs={12} sm={4} key={type}>
                <Card elevation={2}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>
                      {type} Leave
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color={getBalanceColor(used, total)}>
                      {remaining}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Remaining (approved)
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={3} mt={2}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Total</Typography>
                        <Typography variant="h6">{total}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Used</Typography>
                        <Typography variant="h6">{used}</Typography>
                      </Box>
                      {pendingDays > 0 && (
                        <Box>
                          <Typography variant="body2" color="warning.main">Pending</Typography>
                          <Typography variant="h6" color="warning.main">{pendingDays}</Typography>
                        </Box>
                      )}
                    </Box>
                    {pendingDays > 0 && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                        {pendingDays} day{pendingDays > 1 ? 's' : ''} awaiting approval
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {balances.length === 0 && (
            <Grid item xs={12}>
              <EmptyState
                icon={AccountBalance}
                title="No leave balances allocated yet"
                description="Contact your principal."
                dense
              />
            </Grid>
          )}
        </Grid>
      )}

      {/* Tab 1: Apply & History */}
      {activeTab === 1 && (
        <>
          {/* Apply Leave Form */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Apply for Leave</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Leave Type</InputLabel>
                  <Select
                    value={applyForm.leave_type}
                    label="Leave Type"
                    onChange={(e) => setApplyForm({ ...applyForm, leave_type: e.target.value })}
                  >
                    {LEAVE_TYPES.map(type => (
                      <MenuItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={applyForm.start_date}
                  onChange={(e) => setApplyForm({ ...applyForm, start_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={applyForm.end_date}
                  onChange={(e) => setApplyForm({ ...applyForm, end_date: e.target.value })}
                  inputProps={{ min: applyForm.start_date }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Reason"
                  fullWidth
                  value={applyForm.reason}
                  onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleApplyLeave}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Leave Request'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Leave History */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Leave History</Typography>
            {requests.length === 0 ? (
              <EmptyState
                icon={EventBusy}
                title="No leave requests yet"
                description="Use the form above to apply for leave."
                dense
              />
            ) : (
              <ResponsiveDataView
                rows={requests}
                keyExtractor={(req) => req.id}
                renderCard={(req) => (
                  <Card variant="outlined" sx={{ mb: 1.5 }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                        <Chip size="small" label={req.leave_type.charAt(0).toUpperCase() + req.leave_type.slice(1)} variant="outlined" />
                        {getStatusChip(req.status)}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {new Date(req.start_date).toLocaleDateString()} – {new Date(req.end_date).toLocaleDateString()} ({req.num_days}d)
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>{req.reason}</Typography>
                      {req.review_notes && (
                        <Typography variant="caption" color="text.secondary">{req.review_notes}</Typography>
                      )}
                    </CardContent>
                  </Card>
                )}
                table={
                  <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Leave Type</strong></TableCell>
                          <TableCell><strong>From</strong></TableCell>
                          <TableCell><strong>To</strong></TableCell>
                          <TableCell align="center"><strong>Days</strong></TableCell>
                          <TableCell><strong>Reason</strong></TableCell>
                          <TableCell align="center"><strong>Status</strong></TableCell>
                          <TableCell><strong>Review Notes</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {requests.map(req => (
                          <TableRow key={req.id}>
                            <TableCell>
                              <Chip size="small" label={req.leave_type.charAt(0).toUpperCase() + req.leave_type.slice(1)} variant="outlined" />
                            </TableCell>
                            <TableCell>{new Date(req.start_date).toLocaleDateString()}</TableCell>
                            <TableCell>{new Date(req.end_date).toLocaleDateString()}</TableCell>
                            <TableCell align="center">{req.num_days}</TableCell>
                            <TableCell>{req.reason}</TableCell>
                            <TableCell align="center">{getStatusChip(req.status)}</TableCell>
                            <TableCell>{req.review_notes || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                }
              />
            )}
          </Paper>
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LeaveManagement;
